import React, { useState, useEffect } from "react";
import Api from "Api/Api";
import { Modal, Button } from "antd";
import { AntSelect, AntInput, AntDate, AntModal } from "components/AntComponents"
import { Formik } from 'formik';
import { investmentDetailsFormSchema } from './validations';

export function StartupInvestmentDetails({ id, onClose, investorId, dynamicFields }) {
  const INITIAL_FORM_VALUE = {
    investment_type: null,
    ccd_floor: null,
    ccd_cap: null,
    ccd_discount: null,
    valuation_for_last_valued_round: null,
    date_of_investment: null,
    no_of_share: null,
    investment_price_per_share: null,
    current_price_per_share: null,
    investor: null,
    amount: null,
    selectedInvestor: null,
    syndicate: null,
    value_of_unit: null,
  };

  const INVESTMENT_TYPES = [
    "Equity",
    "CCD",
    "CCPS",
    "Safe Notes",
    "Token/USSD",
  ];

  const CURRENCY_TYPES = ["INR", "USD"];

  const [startup_id, setStartup_id] = useState(null);
  const [details, setDetails] = useState(INITIAL_FORM_VALUE);
  const [showPopup, setShowPopup] = useState(false);
  const [investors, setInvestors] = useState([]);
  const [selectedInvestor, setSelectedInvestor] = useState(null);

  const closePopup = () => {
    setDetails(INITIAL_FORM_VALUE);
    setStartup_id(null);
    setShowPopup(false);
    onClose();
  };

  const saveData = async (payload, setSubmitting) => {
    setSubmitting(true);
    let data = payload;
    data.startup = startup_id;
    const response = await Api.updateStartupInvestmentDetail(
      startup_id,
      selectedInvestor,
      payload
    );
    if (response) {
      if (response.status === 200) {
        setSubmitting(false);
        closePopup();
      }
    }
  };

  const loadData = async (investor = null) => {
    setStartup_id(id);
    const response = await Api.getStartupInvestmentDetail(id, investor);
    if (response) {
      const formValues = {
        ...details,
        investor
      }
      if (response.status === 200) {
        if (response.data.length) {
          setDetails({
            ...formValues,
            ...response.data[0]
          });
        } else {
          setDetails(formValues);
        }
      }
    }
  };

  const getInvestorsData = async (startupId) => {
    const investorResponse = await Api.getInvestorsDataFromStartupID(
      startupId,
      "investor"
    );
    if (investorResponse.status === 200) {
      const investors = investorResponse.data.map((record) => record.investor);
      setInvestors(investors);
      const selectedInvestorId = investorId || (investors.length ? investors[0].id : null);
      setSelectedInvestor(selectedInvestorId);
      if (selectedInvestorId) {
        loadData(selectedInvestorId);
      }
    }
  };

  useEffect(() => {
    if (id) {
      if (!investorId) {
        getInvestorsData(id);
      } else {
        setSelectedInvestor(investorId);
        loadData(investorId);
      }
      setShowPopup(true);
    }
  }, [id]);

  return (
    <AntModal title="Investment Details" isOpen={showPopup} onClose={closePopup} footer={null}>
      <div>
        <Formik
          initialValues={details}
          enableReinitialize
          validationSchema={investmentDetailsFormSchema}
          onSubmit={(values, { setSubmitting }) => {
            saveData(values, setSubmitting);
          }}
        >
          {({
            values,
            errors,
            handleSubmit,
            isSubmitting,
            touched,
            setFieldValue,
            /* and other goodies */
          }) => (
            <form onSubmit={handleSubmit}>
              {/* form value--{JSON.stringify(values)} */}
              {/* {JSON.stringify(errors)} */}
              <div className="grid grid-cols-2 gap-10 mb-2">
                {!investorId && <AntSelect
                  options={{
                    allowClear: true
                  }}
                  value={values.investor}
                  dataSource={investors}
                  displayExpr={"username"}
                  valueExpr={"id"}
                  setFieldValue={setFieldValue}
                  fieldName="investor"
                  errors={errors}
                  touched={touched}
                  isRequired
                />}

                <AntSelect
                  options={{
                    allowClear: true
                  }}
                  value={values.investment_type}
                  dataSource={INVESTMENT_TYPES}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                  fieldName="investment_type"
                  isRequired
                />
                <AntSelect
                  options={{
                    allowClear: true
                  }}
                  value={values.currency}
                  dataSource={CURRENCY_TYPES}
                  setFieldValue={setFieldValue}
                  fieldName="currency"
                  errors={errors}
                  touched={touched}
                  isRequired
                />
                {values.investment_type === "CCD" && <AntInput
                  value={values.ccd_cap}
                  setFieldValue={setFieldValue}
                  fieldName={"ccd_cap"}
                  errors={errors}
                  touched={touched}
                  options={{
                    placeholder: "CCD Cap"
                  }}
                />}

                <AntInput
                  value={values.valuation_for_last_valued_round}
                  setFieldValue={setFieldValue}
                  fieldName={"valuation_for_last_valued_round"}
                  errors={errors}
                  touched={touched}
                />
                <AntDate
                  fieldName={"date_of_investment"}
                  options={{
                    format: "YYYY-MM-DD"
                  }}
                  setFieldValue={setFieldValue}
                  value={values.date_of_investment}
                  errors={errors}
                  touched={touched}
                  isRequired
                />
                <AntInput
                  value={values.no_of_share}
                  setFieldValue={setFieldValue}
                  fieldName={"no_of_share"}
                  label={"No of security"}
                  errors={errors}
                  touched={touched}
                />
                {values.investment_type === "CCD" && <AntInput
                  value={values.ccd_floor}
                  setFieldValue={setFieldValue}
                  fieldName={"ccd_floor"}
                  touched={touched}
                  errors={errors}
                />}

                <AntInput
                  value={values.ccd_discount}
                  setFieldValue={setFieldValue}
                  fieldName={"ccd_discount"}
                  touched={touched}
                  errors={errors}
                />
                <AntInput
                  value={values.investment_price_per_share}
                  setFieldValue={setFieldValue}
                  fieldName={"investment_price_per_share"}
                  label={"Investment price per security"}
                  errors={errors}
                  touched={touched}
                />
                <AntInput
                  value={values.current_price_per_share}
                  setFieldValue={setFieldValue}
                  fieldName={"current_price_per_share"}
                  label={"Current price per security"}
                  errors={errors}
                  touched={touched}
                />
                <AntInput
                  value={values.amount}
                  setFieldValue={setFieldValue}
                  fieldName={"amount"}
                  errors={errors}
                  touched={touched}
                  isRequired
                />
                <AntSelect
                  options={{
                    allowClear: true
                  }}
                  value={values.syndicate}
                  dataSource={dynamicFields.syndicate_choices}
                  setFieldValue={setFieldValue}
                  fieldName="syndicate"
                  errors={errors}
                  touched={touched}
                />
                <AntInput
                  value={values.value_of_unit}
                  setFieldValue={setFieldValue}
                  fieldName={"value_of_unit"}
                  errors={errors}
                  touched={touched}
                />
              </div>
              <Button type="primary" htmlType="submit" className="flex flex-row-reverse">Save</Button>

            </form>
          )}
        </Formik>
      </div>
    </AntModal >
  );
}
